import { NavLink, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Package, CreditCard,
  Award, Layers, Wallet, Bell, User as UserIcon,
  Sparkles, ChevronsLeft, Network, Trophy, Star, X, Home,
  Shield, LogOut, WalletCards,
} from "lucide-react";
import clsx from "clsx";

import { useUIStore } from "@/store/ui-store";
import { useAuthStore } from "@/store/auth-store";
import { APP_NAME } from "@/lib/constants";
import { agentApi, starApi } from "@/lib/extensions-api";

const NAV = [
  { to: "/dashboard",               label: "Dashboard",     icon: LayoutDashboard },
  // { to: "/catalog",                 label: "Catalog",       icon: ShoppingBag     },
  { to: "/dashboard/orders",        label: "My Orders",     icon: Package         },
  { to: "/dashboard/payments",      label: "Payments",      icon: CreditCard      },
  { to: "/dashboard/qualification", label: "Qualification", icon: Award           },
  { to: "/dashboard/membership",    label: "Membership",    icon: Sparkles        },
  { to: "/dashboard/entries",       label: "My Club",       icon: Layers          },
  { to: "/dashboard/wallet",        label: "Wallet",        icon: Wallet          },
  { to: "/dashboard/wallet/withdraw", label: "Withdraw",        icon: WalletCards },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell            },
  { to: "/dashboard/profile",       label: "Profile",       icon: UserIcon        },
  { to: "/dashboard/referrals",     label: "Referrals",     icon: Network         },
  { to: "/dashboard/star",          label: "Star Member",   icon: Trophy          },
];

export function Sidebar() {
  const { mobileMenuOpen, closeMobileMenu } = useUIStore();
  const { user, isAuthenticated, logout } = useAuthStore();
  const nav = useNavigate(); // 🛠️ ADDED useNavigate

  const { data: starStatus } = useQuery({ 
    queryKey: ["star-status-sidebar"], 
    queryFn: starApi.status,
    enabled: isAuthenticated,
    staleTime: 60000 
  });

  const { data: agentStatus } = useQuery({
    queryKey: ["agent-status-sidebar"],
    queryFn: agentApi.checkStatus,
    enabled: isAuthenticated,
    staleTime: 60000
  });

  const isStarMember = starStatus?.is_star_member;
  const isInactive = user?.status === "inactive";
  const isAgent = agentStatus?.is_agent;

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    nav("/login");
  };

  return (
    <>
      {/* 📱 Backdrop Overlay (Visible when drawer is open) */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-surface-900/60 backdrop-blur-sm transition-opacity" 
          onClick={closeMobileMenu} 
        />
      )}

      {/* 💻 Sidebar Drawer (Floats OVER the page, Full Collapsible) */}
      <aside className={clsx(
        // 🛠️ FIXED: Added pb-20 to ensure the bottom of the sidebar is not hidden by the mobile BottomNav!
        "fixed top-0 left-0 z-50 h-screen w-[280px] bg-white border-r border-surface-200 flex flex-col transition-transform duration-300 shadow-2xl pb-20 lg:pb-0",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        
        {/* App Logo Header */}
        <div className="flex flex-col border-b border-surface-200 shrink-0 bg-white">
          <div className="h-16 flex items-center justify-between px-5">
            <div className="flex items-center gap-3 overflow-hidden">
              
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="h-10 w-auto object-contain shrink-0" 
              />
              
            </div>
            {/* Close Button */}
            <button onClick={closeMobileMenu} className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 transition-colors">
              <ChevronsLeft className="h-5 w-5" />
            </button>
          </div>          
        </div>

        {/* 🌟 LUXURIOUS USER PROFILE SECTION */}
        {user && (
          <div className="p-5 border-b border-surface-800 bg-[#333333] text-white relative overflow-hidden shrink-0">
            {/* Background subtle glow if Star Member */}
            {isStarMember && <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-2xl" />}
            
            <div className="flex items-center gap-4 relative z-10">
              
              {/* Profile Image with Thick Border */}
              <div className="shrink-0 relative">
                {user.profile_image ? (
                  <img src={user.profile_image} className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg bg-surface-200" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-xl font-bold border-4 border-white shadow-lg">
                    {user.full_name.charAt(0)}
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm tracking-wide truncate">{user.full_name.toUpperCase()}</div>
                
                {/* 🌟 Dynamic Status Line */}
                <div className={clsx(
                  "font-serif text-lg font-bold tracking-wide mt-0.5",
                  isStarMember ? "text-[#E6C27A]" : isInactive ? "text-danger-400" : "text-brand-300"
                )}>
                  {isStarMember ? "Star Member" : isInactive ? "Inactive Member" : "Active Member"}
                </div>
                
                <div className="text-xs font-medium text-surface-300 mt-1 flex items-center gap-1">
                  ID: <span className="font-mono">{user.user_id}</span>
                </div>

                {/* 5-Star Rating Array */}
                <div className="flex items-center gap-0.5 mt-2">
                  {[1, 2, 3, 4, 5].map((starIndex) => {
                    const isMiddle = starIndex === 3;
                    const isFilled = isStarMember || (!isInactive && starIndex === 1); // 1 star for Active, 5 for Star
                    return (
                      <Star 
                        key={starIndex} 
                        className={clsx(
                          isMiddle ? "w-5 h-5 -translate-y-1 drop-shadow-md" : "w-3.5 h-3.5",
                          isFilled ? "fill-[#E6C27A] text-[#E6C27A]" : "fill-surface-600 text-surface-600 opacity-50"
                        )} 
                      />
                    );
                  })}
                </div>
              </div>

              {/* 🏆 Golden Ribbon Badge (For Star Members) */}
              {isStarMember && (
                <div className="absolute right-0 bottom-0 opacity-90 drop-shadow-xl transform translate-x-2 translate-y-2">
                  <svg width="48" height="56" viewBox="0 0 48 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0ZM24 44C12.9543 44 4 35.0457 4 24C4 12.9543 12.9543 4 24 4C35.0457 4 44 12.9543 44 24C44 35.0457 35.0457 44 24 44Z" fill="url(#paint0_linear)"/>
                    <circle cx="24" cy="24" r="18" fill="url(#paint1_linear)"/>
                    <path d="M12 42L8 56L24 50L40 56L36 42" fill="url(#paint2_linear)"/>
                    <defs>
                      <linearGradient id="paint0_linear" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FDE68A"/>
                        <stop offset="0.5" stopColor="#D97706"/>
                        <stop offset="1" stopColor="#FDE68A"/>
                      </linearGradient>
                      <linearGradient id="paint1_linear" x1="6" y1="6" x2="42" y2="42" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FEF3C7"/>
                        <stop offset="0.5" stopColor="#F59E0B"/>
                        <stop offset="1" stopColor="#B45309"/>
                      </linearGradient>
                      <linearGradient id="paint2_linear" x1="8" y1="42" x2="40" y2="56" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FDE68A"/>
                        <stop offset="0.5" stopColor="#D97706"/>
                        <stop offset="1" stopColor="#FDE68A"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              )}
            </div>
          </div>
        )}


        {/* 🛠️ Action Buttons (Under Profile) */}
        <div className="px-4 py-4 flex flex-col gap-2 border-b border-surface-200 bg-surface-50 shrink-0">
          <Link to="/" onClick={closeMobileMenu}>
            <button className="w-full flex items-center justify-center gap-2 bg-white hover:bg-surface-100 text-surface-700 font-semibold text-xs py-2 rounded-lg transition-colors border border-surface-300 shadow-sm">
              <Home className="w-3.5 h-3.5" /> Back to Storefront
            </button>
          </Link>

          {isAgent && (
            <Link to="/agent" onClick={closeMobileMenu}>
              <button className="w-full flex items-center justify-center gap-2 bg-warning-100 hover:bg-warning-200 text-warning-800 font-semibold text-xs py-2 rounded-lg transition-colors border border-warning-200">
                <Shield className="w-3.5 h-3.5" /> Go to Agent Portal
              </button>
            </Link>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/dashboard"}
              onClick={closeMobileMenu} // Auto-close drawer on click
              className={({ isActive }) => clsx(
                "flex items-center gap-3.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all",
                isActive ? "bg-brand-50 text-brand-700 shadow-sm ring-1 ring-brand-200/50" : "text-surface-600 hover:bg-surface-100 hover:text-surface-900",
              )}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        {/* 🛠️ NEW: Logout Section at the very bottom */}
        <div className="p-4 border-t border-surface-200 shrink-0 bg-surface-50">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3.5 px-3 py-3 rounded-xl text-sm font-semibold text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-all"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}